import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./config";

export type ReceptionPresenceValue = "present" | "absent";

export type ClinicReceptionPresence = {
    clinicId: number;
    clinicName: string;
    isReceptionOnSite: boolean;
    source: "manual" | "schedule";
};

type ReceptionPresenceToggle = {
    clinicId: number;
    presence: ReceptionPresenceValue;
};

const RECEPTION_PRESENCE_QUERY_KEY = ["reception-presence"];
const RECEPTION_PRESENCE_REFRESH_MS = 30 * 1000;

const fetchReceptionPresence = async () => {
    const response = await api.get("/kiosk-check-in/reception-presence");
    const clinicsPresence = response.data.data as Array<ClinicReceptionPresence>;
    return clinicsPresence;
};

const postReceptionPresenceToggle = async ({ clinicId, presence }: ReceptionPresenceToggle) => {
    const response = await api.post("/kiosk-check-in/reception-presence", { clinicId, presence });
    const clinicsPresence = response.data.data as Array<ClinicReceptionPresence>;
    return clinicsPresence;
};

export const useReceptionPresence = (isEnabled: boolean) => {
    const receptionPresenceQuery = useQuery({
        queryKey: RECEPTION_PRESENCE_QUERY_KEY,
        queryFn: fetchReceptionPresence,
        enabled: isEnabled,
        refetchInterval: RECEPTION_PRESENCE_REFRESH_MS,
        refetchOnMount: "always" as const,
    });
    return receptionPresenceQuery;
};

export const useToggleReceptionPresence = () => {
    const queryClient = useQueryClient();

    const toggleMutation = useMutation({
        mutationFn: postReceptionPresenceToggle,
        onSuccess: (clinicsPresence) => queryClient.setQueryData(RECEPTION_PRESENCE_QUERY_KEY, clinicsPresence),
    });
    return toggleMutation;
};
